import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { NumericFormat } from 'react-number-format';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import {
  FaFileAlt, FaSearch, FaUser, FaCar, FaPrint, FaTimes, FaCheckCircle, FaArrowLeft, FaMapMarkerAlt,
  FaBold, FaItalic, FaUnderline, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify,
  FaUndo, FaRedo, FaEraser, FaCog, FaPlus
} from 'react-icons/fa';
import Header from '../others/Header';
import config from '../config/config';
import { useToast } from '../components/ToastContext';
import ModalConfiguracaoModelo from '../components/ModalConfiguracaoModelo';
import { sanitizarHtml } from '../others/sanitizarHtml';
import ConfirmModal from '../components/ConfirmModal';
import BarraCadastro from '../components/BarraCadastro';
import AvisoCadastroIncompleto from '../components/AvisoCadastroIncompleto';
import ImportarDocumento from '../components/ImportarDocumento';
import { resolverAnexo } from '../others/importacao';
import { anexarDocumentoCliente, anexarDocumentoVeiculo } from '../others/anexos';
import { situacaoCadastro, buscarClientePorDocumento, buscarVeiculoPorPlaca as buscarVeiculoCadastradoPorPlaca } from '../others/cadastroSync';
import {
  pessoaVazia, veiculoVazio, clienteParaPessoa, removerMascaraCPFCNPJ, formatarCpfCnpj, formatarCep, buscarCep,
  EnderecoCampos, PageBackground, Content, TitleRow, Title, Card, CardHeader, CardTitle, SubTitle, SearchButton,
  FormRow, InputGroup, Label, HintText, ErrorText, Input, EmptyState, ActionsRow, PrimaryButton, SecondaryButton,
  PreviewWrapper, PreviewToolbar, PrintButton, SelectField, ModelosGrid, ModeloCard, EditorToolbar, FerramentaBtn,
  Separador, PaperSheet, ModalOverlay, ModalBox, ModalHeader, ModalClose, ModalContent, SearchRow, ResultList, ResultItem
} from './Procuracao';
import { MODELOS, MODELOS_POR_ID, GRUPOS, extrasIniciais, gerarDocumentoHtml } from '../others/documentosComplementares';

const TOKENS_DOC_COMPLEMENTAR = ['DECLARANTE', 'COMPRADOR', 'VEICULO_PLACA', 'VEICULO_CHASSI', 'VEICULO_MARCA_MODELO', 'LOCAL', 'DATA'];

// Dados de exemplo usados só para gerar o texto padrão de fábrica (seed do editor em Configurações)
const ctxExemplo = (modelo) => ({
  declarante: { nome: 'JOÃO DA SILVA', cpfCnpj: '12345678900' },
  comprador: { nome: 'MARIA SOUZA', cpfCnpj: '98765432100' },
  veiculo: { placa: 'ABC-1234', chassi: '9BWZZZ377VT004251', marcaModelo: 'VW GOL', cor: 'PRETA', renavam: '123456789' },
  extras: extrasIniciais(modelo),
  local: 'Porto Alegre',
  dataISO: new Date().toISOString().slice(0, 10)
});

const getAuthHeader = () => {
  const token = localStorage.getItem('jwtToken');
  return { headers: { Authorization: `Bearer ${token}` } };
};

const veiculoDoCadastro = (v) => ({
  idVeiculo: v.idVeiculo,
  marcaModelo: v.marcaModelo || '',
  cor: v.cor || '',
  placa: v.placa || '',
  chassi: v.chassi || '',
  renavam: v.renavam || '',
  ano: v.ano || '',
  motor: v.motor || ''
});

const CAMPOS_PESSOA = ['nome', 'cpfCnpj', 'rg', 'telefone', 'email', 'cep', 'logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf'];
const CAMPOS_VEICULO_SYNC = ['placa', 'chassi', 'renavam', 'marcaModelo', 'cor', 'ano', 'motor'];
const ROTULOS_CAMPO_PESSOA = {
  nome: 'nome', cpfCnpj: 'CPF/CNPJ', rg: 'RG', telefone: 'telefone', email: 'e-mail', cep: 'CEP',
  logradouro: 'logradouro', numero: 'número', complemento: 'complemento', bairro: 'bairro', cidade: 'cidade', uf: 'UF'
};
const ROTULOS_CAMPO_VEICULO = {
  placa: 'placa', chassi: 'chassi', renavam: 'RENAVAM', marcaModelo: 'marca/modelo', cor: 'cor', ano: 'ano',
  motor: 'nº do motor'
};

// ─── Rota: lista de documentos ou o formulário do documento escolhido ─────────

const DocumentosComplementares = () => {
  const { tipo } = useParams();
  if (!tipo) return <ListaDocumentos />;
  const modelo = MODELOS_POR_ID[tipo];
  if (!modelo) return <Navigate to="/documentos-complementares" replace />;
  return <FormDocumento key={modelo.id} modelo={modelo} />;
};

// ─── Lista: cada documento é um item separado ─────────────────────────────────

const ListaDocumentos = () => {
  const navigate = useNavigate();
  const [filtro, setFiltro] = useState('');
  const [modalConfigAberto, setModalConfigAberto] = useState(false);
  const termo = filtro.trim().toLowerCase();
  const visiveis = MODELOS.filter((m) => !termo || `${m.titulo} ${m.sub}`.toLowerCase().includes(termo));

  // Requerimentos com formulário do DETRAN (formularioDetran) não têm texto para personalizar
  const modelosConfiguracao = MODELOS.filter((m) => !m.formularioDetran).map((m) => ({ tipo: m.id, titulo: m.titulo }));
  const buscarTextoPadraoDoc = async (tipo) => {
    const modelo = MODELOS_POR_ID[tipo];
    return gerarDocumentoHtml(modelo, ctxExemplo(modelo));
  };

  return (
    <PageBackground>
      <Header />
      <Content>
        <TitleRow>
          <Title><FaFileAlt size={22} /> Documentos Complementares</Title>
          <SecondaryButton type="button" onClick={() => setModalConfigAberto(true)} title="Personalizar texto dos modelos">
            <FaCog /> Configurações
          </SecondaryButton>
        </TitleRow>

        {modalConfigAberto && (
          <ModalConfiguracaoModelo
            categoria="DOCUMENTO_COMPLEMENTAR"
            modelos={modelosConfiguracao}
            tokensAjuda={TOKENS_DOC_COMPLEMENTAR}
            buscarTextoPadrao={buscarTextoPadraoDoc}
            onClose={() => setModalConfigAberto(false)}
          />
        )}

        <Card>
          <InputGroup>
            <Label>Pesquisar documento</Label>
            <Input value={filtro} onChange={(ev) => setFiltro(ev.target.value)} placeholder="Ex.: chassi, plaqueta, baixa..." />
          </InputGroup>
        </Card>

        {visiveis.length === 0 && <EmptyState>Nenhum documento encontrado.</EmptyState>}

        {GRUPOS.map((grupo) => {
          const itens = visiveis.filter((m) => m.grupo === grupo);
          if (itens.length === 0) return null;
          return (
            <Card key={grupo}>
              <CardHeader>
                <CardTitle><FaFileAlt size={16} /> {grupo}</CardTitle>
              </CardHeader>
              <ModelosGrid>
                {itens.map((m) => (
                  <ModeloCard key={m.id} type="button" onClick={() => navigate(`/documentos-complementares/${m.id}`)}>
                    <strong>{m.titulo}</strong>
                    <span>{m.sub}</span>
                  </ModeloCard>
                ))}
              </ModelosGrid>
            </Card>
          );
        })}
      </Content>
    </PageBackground>
  );
};

// ─── Formulário de um documento ───────────────────────────────────────────────

const FormDocumento = ({ modelo }) => {
  const { API_BASE_URL } = config;
  const { addToast } = useToast();
  const navigate = useNavigate();
  const previewRef = useRef(null);
  const editorRef = useRef(null);
  const editadoRef = useRef(false);

  const [declarante, setDeclarante] = useState(pessoaVazia());
  const [comprador, setComprador] = useState(pessoaVazia());
  const [veiculosDoCliente, setVeiculosDoCliente] = useState([]);
  const [veiculo, setVeiculo] = useState(veiculoVazio());
  const [importacaoVeiculo, setImportacaoVeiculo] = useState(null); // CRLV/ATPV importado (a ser anexado ao veículo ao salvar)
  // Recibos de motor/carroceria: veículo de onde a peça saiu (só preenche o documento, não é gravado)
  const [veiculoOrigem, setVeiculoOrigem] = useState(veiculoVazio());
  const [importacaoOrigem, setImportacaoOrigem] = useState(null);
  const [alvoVeiculo, setAlvoVeiculo] = useState('principal'); // qual veículo o modal de busca preenche
  const [extras, setExtras] = useState(() => extrasIniciais(modelo));
  // Modelos ligados ao motor (morfologia, remarcação, recibo de motor) pedem o nº do motor como campo do documento
  // (chave 'numeroMotor' em documentosComplementares.js), e ele também é cadastro do veículo (campo 'motor').
  // O input é um só — o campo extra —, espelhado aqui em veiculo.motor, que é o que vai para o cadastro e o que é
  // comparado com ele. Os dois têm de andar juntos: se divergirem, o documento imprimiria um número e o cadastro outro.
  const documentoPedeMotor = modelo.campos.some((c) => c.chave === 'numeroMotor');
  const [dataDocumento, setDataDocumento] = useState('');
  const [local, setLocal] = useState('');

  const [errosDeclarante, setErrosDeclarante] = useState({});
  const [errosComprador, setErrosComprador] = useState({});
  const [erroVeiculo, setErroVeiculo] = useState('');
  const [errosExtras, setErrosExtras] = useState({}); // campos do documento marcados como obrigatórios

  // Snapshot do que está gravado no cadastro, pra comparar com o que está preenchido aqui
  // e mostrar a situação (novo/alterado/igual) — mesmo padrão do Procuração/Contratos.
  const [origem, setOrigem] = useState({ declarante: null, comprador: null, veiculo: null });
  const [salvandoDeclarante, setSalvandoDeclarante] = useState(false);
  const [salvandoComprador, setSalvandoComprador] = useState(false);
  const [salvandoVeiculo, setSalvandoVeiculo] = useState(false);
  const [confirmacaoCadastro, setConfirmacaoCadastro] = useState(null);

  const [documentoHtml, setDocumentoHtml] = useState('');
  const [versaoDocumento, setVersaoDocumento] = useState(0);
  const [mostrarPreview, setMostrarPreview] = useState(false);

  // Texto customizado pela empresa em Configurações para este modelo (undefined = usa o padrão)
  const [textoCustomizado, setTextoCustomizado] = useState(null);
  useEffect(() => {
    let ativo = true;
    axios.get(`${API_BASE_URL}/modelos-documento-texto`, {
      ...getAuthHeader(),
      params: { categoria: 'DOCUMENTO_COMPLEMENTAR', modelo: modelo.id }
    }).then((resp) => {
      if (ativo && resp.status === 200 && resp.data?.texto != null) setTextoCustomizado(resp.data.texto);
    }).catch(() => {});
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modelo.id]);

  const [modalBuscaAberto, setModalBuscaAberto] = useState(false);
  const [buscaAlvo, setBuscaAlvo] = useState('declarante'); // 'declarante' | 'comprador'
  const [termoBusca, setTermoBusca] = useState('');
  const [resultadosBusca, setResultadosBusca] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [buscandoCep, setBuscandoCep] = useState(null); // 'declarante' | 'comprador' | null

  const [modalVeiculoAberto, setModalVeiculoAberto] = useState(false);
  const [placaBusca, setPlacaBusca] = useState('');
  const [buscandoPlaca, setBuscandoPlaca] = useState(false);
  const [resultadoPlaca, setResultadoPlaca] = useState(null);
  const [erroPlaca, setErroPlaca] = useState('');

  // Cidade-UF do escritório: o documento sai "Canguçu-RS, 21 de março de 2026"
  useEffect(() => {
    let ativo = true;
    axios.get(`${API_BASE_URL}/empresas/minha`, getAuthHeader())
      .then((resp) => {
        if (!ativo || !resp || !resp.data) return;
        const cidadeUf = [resp.data.cidade, resp.data.uf].filter(Boolean).join('-');
        if (cidadeUf) setLocal((atual) => atual || cidadeUf);
      })
      .catch(() => { /* o local fica em branco para preencher */ });
    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Busca de cliente (declarante e adquirente) ───────────────────────────
  const abrirBusca = (alvo) => {
    setBuscaAlvo(alvo);
    setTermoBusca('');
    setResultadosBusca([]);
    setModalBuscaAberto(true);
  };

  const executarBusca = async () => {
    const termo = termoBusca.trim();
    if (termo.length < 3) {
      addToast('warning', 'Informe ao menos 3 caracteres para pesquisar.');
      return;
    }
    setBuscando(true);
    setResultadosBusca([]);
    try {
      try {
        const respCpf = await axios.get(`${API_BASE_URL}/clientes/cpfcnpj/${removerMascaraCPFCNPJ(termo)}`, getAuthHeader());
        if (respCpf.data) {
          setResultadosBusca([respCpf.data]);
          return;
        }
      } catch (err) {
        // não encontrou por CPF/CNPJ, tenta por nome
      }
      const respNome = await axios.get(`${API_BASE_URL}/clientes/nome/${termo.toLowerCase()}`, getAuthHeader());
      if (respNome.data && respNome.data.length > 0) setResultadosBusca(respNome.data);
    } catch (error) {
      console.error('Erro na pesquisa de clientes:', error);
      addToast('error', 'Erro ao pesquisar clientes.');
    } finally {
      setBuscando(false);
    }
  };

  const selecionarResultado = async (clienteResumo) => {
    setBuscando(true);
    try {
      const resp = await axios.get(`${API_BASE_URL}/clientes/${clienteResumo.idCliente}`, getAuthHeader());
      const cliente = resp.data;
      const pessoa = clienteParaPessoa(cliente);
      if (buscaAlvo === 'declarante') {
        setDeclarante(pessoa);
        setErrosDeclarante({});
        setOrigem((o) => ({ ...o, declarante: pessoa }));
        // Os veículos do cliente ficam só como opção em "Selecionar Veículo": o veículo do documento é
        // preenchido apenas quando o usuário escolhe um ou importa o documento (CRLV/ATPV)
        setVeiculosDoCliente(Array.isArray(cliente.veiculos) ? cliente.veiculos : []);
      } else {
        setComprador(pessoa);
        setErrosComprador({});
        setOrigem((o) => ({ ...o, comprador: pessoa }));
      }
      setModalBuscaAberto(false);
    } catch (error) {
      console.error('Erro ao carregar cliente:', error);
      addToast('error', 'Erro ao carregar dados do cliente.');
    } finally {
      setBuscando(false);
    }
  };

  const atualizarPessoa = (setPessoa, setErros) => (campo, valor) => {
    setPessoa((prev) => ({ ...prev, [campo]: valor }));
    setErros((prev) => ({ ...prev, [campo]: undefined }));
  };
  const atualizarDeclarante = atualizarPessoa(setDeclarante, setErrosDeclarante);
  const atualizarComprador = atualizarPessoa(setComprador, setErrosComprador);

  const mudarCep = (alvo, setPessoa) => async (valor) => {
    const formatado = formatarCep(valor);
    setPessoa((prev) => ({ ...prev, cep: formatado }));
    if (formatado.replace(/\D/g, '').length === 8) {
      setBuscandoCep(alvo);
      const dados = await buscarCep(formatado);
      if (dados) setPessoa((prev) => ({ ...prev, ...dados }));
      setBuscandoCep(null);
    }
  };

  // ── Veículo ──────────────────────────────────────────────────────────────

  // ── Importar dados do CRLV/ATPV e guardar o arquivo no cadastro do veículo ──
  const anexarImportacaoVeiculo = async (idVeiculo, placaSalva) => {
    const { arquivo, divergente } = resolverAnexo(importacaoVeiculo, placaSalva);
    setImportacaoVeiculo(null);
    if (divergente) {
      addToast('warning', 'O documento importado é de outra placa; não foi anexado a este veículo. Anexe-o pelos documentos do veículo, se for o caso.');
      return;
    }
    if (!arquivo) return;
    const anexou = await anexarDocumentoVeiculo(API_BASE_URL, idVeiculo, arquivo, getAuthHeader());
    addToast(
      anexou ? 'success' : 'warning',
      anexou ? 'Documento guardado nos documentos do veículo.' : 'Veículo salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do veículo.'
    );
  };

  const aplicarDocumentoVeiculo = (dados) => {
    setVeiculo((prev) => ({ ...prev, ...dados }));
    setErroVeiculo('');
  };

  const atualizarVeiculo = (campo, valor) => {
    setVeiculo((prev) => ({ ...prev, [campo]: valor }));
    setErroVeiculo('');
  };

  const abrirModalVeiculo = (alvo = 'principal') => {
    setAlvoVeiculo(alvo);
    setPlacaBusca('');
    setResultadoPlaca(null);
    setErroPlaca('');
    setModalVeiculoAberto(true);
  };

  const buscarVeiculoPorPlaca = async () => {
    const placa = placaBusca.trim().toUpperCase();
    if (!placa) return;
    setBuscandoPlaca(true);
    setResultadoPlaca(null);
    setErroPlaca('');
    try {
      const resp = await axios.get(`${API_BASE_URL}/veiculos/placa/${placa}`, getAuthHeader());
      setResultadoPlaca(resp.data);
    } catch (error) {
      if (error.response?.status === 404) {
        setErroPlaca('Nenhum veículo cadastrado com essa placa.');
      } else {
        console.error('Erro ao buscar veículo por placa:', error);
        setErroPlaca('Erro ao buscar veículo. Tente novamente.');
      }
    } finally {
      setBuscandoPlaca(false);
    }
  };

  const importarVeiculo = (v) => {
    const dados = veiculoDoCadastro(v);
    if (alvoVeiculo === 'origem') {
      setVeiculoOrigem(dados);
      setModalVeiculoAberto(false);
      addToast('success', 'Dados do veículo de origem preenchidos.');
      return;
    }
    // O nº do motor do cadastro vence; se o cadastro não tem, mantém o que já foi digitado no documento.
    // "origem" continua sendo o que está no cadastro, para o aviso de dado novo/diferente funcionar.
    const motor = documentoPedeMotor ? (dados.motor || extras.numeroMotor || '') : dados.motor;
    setVeiculo({ ...dados, motor });
    setImportacaoVeiculo(null);
    setOrigem((o) => ({ ...o, veiculo: dados }));
    if (documentoPedeMotor) setExtras((prev) => ({ ...prev, numeroMotor: motor }));
    setErroVeiculo('');
    setModalVeiculoAberto(false);
    addToast('success', 'Dados do veículo importados.');
  };

  // ── Salvar/atualizar o cadastro de cliente a partir dos dados preenchidos aqui ──
  // Se já tem idCliente, atualiza só os campos preenchidos (nunca apaga o que já está no
  // cadastro); senão, verifica se já existe um cliente com o mesmo CPF/CNPJ (evita duplicar)
  // e cria um novo caso não exista.
  const payloadPessoa = (pessoa) => ({
    nome: pessoa.nome, cpfCnpj: pessoa.cpfCnpj.replace(/\D/g, ''), rg: pessoa.rg, telefone: pessoa.telefone, email: pessoa.email,
    cep: pessoa.cep, logradouro: pessoa.logradouro, numero: pessoa.numero, complemento: pessoa.complemento,
    bairro: pessoa.bairro, cidade: pessoa.cidade, uf: pessoa.uf
  });

  // Guarda no cadastro do cliente o documento importado (CNH/RG) junto da pessoa, se o usuário quis guardar
  const anexarImportacao = async (idCliente, importacao, cpfSalvo) => {
    const { arquivo, divergente } = resolverAnexo(importacao, cpfSalvo);
    if (divergente) {
      addToast('warning', 'O documento importado é de outro CPF/CNPJ; não foi anexado a este cliente. Anexe-o pelos documentos do cliente, se for o caso.');
      return;
    }
    if (!arquivo) return;
    const anexou = await anexarDocumentoCliente(API_BASE_URL, idCliente, arquivo, getAuthHeader());
    addToast(
      anexou ? 'success' : 'warning',
      anexou ? 'Documento guardado nos documentos do cliente.' : 'Cadastro salvo, mas não foi possível guardar o documento. Anexe-o pelos documentos do cliente.'
    );
  };

  const salvarClienteNoCadastro = (alvo) => {
    const config2 = alvo === 'declarante'
      ? { pessoa: declarante, setPessoa: setDeclarante, setSalvando: setSalvandoDeclarante, rotulo: 'declarante' }
      : { pessoa: comprador, setPessoa: setComprador, setSalvando: setSalvandoComprador, rotulo: 'comprador' };
    const { pessoa, setPessoa, setSalvando, rotulo } = config2;

    const complementar = async (idCliente) => {
      setSalvando(true);
      try {
        const response = await axios.put(`${API_BASE_URL}/clientes/${idCliente}/complementar`, payloadPessoa(pessoa), getAuthHeader());
        const dados = clienteParaPessoa(response.data);
        setPessoa(dados);
        setOrigem((o) => ({ ...o, [alvo]: dados }));
        addToast('success', `Cadastro do ${rotulo} atualizado com sucesso!`);
        await anexarImportacao(idCliente, pessoa._importacao, response.data.cpfCnpj);
      } catch (error) {
        console.error(`Erro ao atualizar cliente (${rotulo}):`, error);
        addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
      } finally {
        setSalvando(false);
      }
    };

    return async () => {
      if (!pessoa.nome || !pessoa.cpfCnpj) {
        addToast('warning', 'Preencha ao menos nome e CPF/CNPJ antes de salvar.');
        return;
      }
      if (pessoa.idCliente) {
        await complementar(pessoa.idCliente);
        return;
      }
      setSalvando(true);
      try {
        const existente = await buscarClientePorDocumento(pessoa.cpfCnpj);
        if (existente) {
          setSalvando(false);
          setConfirmacaoCadastro({
            titulo: 'Cliente já cadastrado',
            mensagem: `Já existe um cliente com este CPF/CNPJ: ${existente.nome}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
            textoConfirmar: 'Atualizar',
            onConfirm: () => complementar(existente.idCliente)
          });
          return;
        }
        const response = await axios.post(`${API_BASE_URL}/clientes`, { ...payloadPessoa(pessoa), ativo: true }, getAuthHeader());
        const dados = clienteParaPessoa(response.data);
        setPessoa(dados);
        setOrigem((o) => ({ ...o, [alvo]: dados }));
        addToast('success', `${rotulo[0].toUpperCase()}${rotulo.slice(1)} cadastrado com sucesso!`);
        await anexarImportacao(response.data.idCliente, pessoa._importacao, response.data.cpfCnpj);
      } catch (error) {
        console.error(`Erro ao cadastrar cliente (${rotulo}):`, error);
        addToast('error', 'Erro ao salvar dados do cliente: ' + (error.response?.data?.message || error.message));
      } finally {
        setSalvando(false);
      }
    };
  };

  // ── Salvar/atualizar o cadastro de veículo a partir dos dados preenchidos aqui ──
  // O veículo fica vinculado ao declarante (proprietário/vendedor nesses modelos).
  const payloadVeiculo = () => ({
    placa: veiculo.placa.trim().toUpperCase(), chassi: veiculo.chassi, renavam: veiculo.renavam,
    marcaModelo: veiculo.marcaModelo, ano: veiculo.ano, cor: veiculo.cor, motor: veiculo.motor
  });

  const aplicarVeiculoSalvo = (dadosVeiculo) => {
    const dados = veiculoDoCadastro(dadosVeiculo);
    setVeiculo(dados);
    setOrigem((o) => ({ ...o, veiculo: dados }));
    // O backend grava o nº do motor em maiúsculas: o campo do documento passa a mostrar o valor como ficou salvo
    if (documentoPedeMotor) setExtras((prev) => ({ ...prev, numeroMotor: dados.motor }));
    if (declarante.idCliente) {
      axios.get(`${API_BASE_URL}/clientes/${declarante.idCliente}`, getAuthHeader())
        .then((r) => setVeiculosDoCliente(Array.isArray(r.data.veiculos) ? r.data.veiculos : []))
        .catch(() => {});
    }
  };

  const complementarVeiculoNoCadastro = async (idVeiculo) => {
    setSalvandoVeiculo(true);
    try {
      const response = await axios.put(`${API_BASE_URL}/veiculos/${idVeiculo}/complementar`, payloadVeiculo(), getAuthHeader());
      aplicarVeiculoSalvo(response.data);
      addToast('success', 'Cadastro do veículo atualizado com sucesso!');
      await anexarImportacaoVeiculo(response.data.idVeiculo || idVeiculo, response.data.placa);
    } catch (error) {
      console.error('Erro ao atualizar veículo:', error);
      addToast('error', 'Erro ao salvar dados do veículo: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoVeiculo(false);
    }
  };

  const salvarVeiculoNoCadastro = async () => {
    const placa = veiculo.placa.trim();
    if (!placa || !veiculo.marcaModelo.trim()) {
      addToast('warning', 'Informe ao menos a placa e a marca/modelo para salvar no cadastro.');
      return;
    }
    if (veiculo.idVeiculo) {
      await complementarVeiculoNoCadastro(veiculo.idVeiculo);
      return;
    }
    setSalvandoVeiculo(true);
    try {
      const existente = await buscarVeiculoCadastradoPorPlaca(placa);
      if (existente) {
        setSalvandoVeiculo(false);
        setConfirmacaoCadastro({
          titulo: 'Veículo já cadastrado',
          mensagem: `Já existe um veículo com esta placa: ${existente.marcaModelo || existente.placa}${existente.nomeCliente ? ` (cliente ${existente.nomeCliente})` : ''}. Atualizar o cadastro dele com os dados preenchidos aqui?`,
          textoConfirmar: 'Atualizar',
          onConfirm: () => complementarVeiculoNoCadastro(existente.idVeiculo)
        });
        return;
      }
      if (!declarante.idCliente) {
        addToast('warning', 'Salve primeiro os dados do declarante no cadastro: o veículo fica vinculado a ele.');
        return;
      }
      const response = await axios.post(`${API_BASE_URL}/veiculos`, { ...payloadVeiculo(), cliente: { idCliente: declarante.idCliente } }, getAuthHeader());
      aplicarVeiculoSalvo(response.data);
      addToast('success', 'Veículo cadastrado com sucesso!');
      await anexarImportacaoVeiculo(response.data.idVeiculo, response.data.placa);
    } catch (error) {
      console.error('Erro ao cadastrar veículo:', error);
      addToast('error', 'Erro ao salvar dados do veículo: ' + (error.response?.data?.message || error.message));
    } finally {
      setSalvandoVeiculo(false);
    }
  };

  // ── Campos extras do modelo ──────────────────────────────────────────────
  const atualizarExtra = (chave, valor) => {
    setExtras((prev) => ({ ...prev, [chave]: valor }));
    setErrosExtras((prev) => (prev[chave] ? { ...prev, [chave]: '' } : prev));
    if (chave === 'numeroMotor') setVeiculo((prev) => ({ ...prev, motor: valor }));
  };

  const alternarOpcao = (chave, valor) => {
    setExtras((prev) => {
      const atuais = prev[chave] || [];
      return { ...prev, [chave]: atuais.includes(valor) ? atuais.filter((x) => x !== valor) : [...atuais, valor] };
    });
  };

  const renderCampoExtra = (c) => {
    const valor = extras[c.chave];
    // Lista de itens (ex.: peças): uma linha por item, com + para abrir mais uma e × para remover
    if (c.tipo === 'lista') {
      const itens = Array.isArray(valor) && valor.length ? valor : [''];
      const mudarItem = (i, texto) => atualizarExtra(c.chave, itens.map((x, j) => (j === i ? texto : x)));
      const removerItem = (i) => atualizarExtra(c.chave, itens.length > 1 ? itens.filter((_, j) => j !== i) : ['']);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {itens.map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 22, textAlign: 'right', color: '#7f8c8d', fontSize: 13 }}>{i + 1}.</span>
              <Input
                style={{ flex: 1 }}
                value={item}
                onChange={(ev) => mudarItem(i, ev.target.value)}
                onKeyDown={(ev) => {
                  // Enter na última linha preenchida abre a próxima
                  if (ev.key === 'Enter' && i === itens.length - 1 && item.trim()) {
                    ev.preventDefault();
                    atualizarExtra(c.chave, [...itens, '']);
                  }
                }}
                placeholder={c.placeholder || ''}
                autoFocus={i > 0 && i === itens.length - 1 && !item}
              />
              <BotaoLista type="button" onClick={() => removerItem(i)} title="Remover" disabled={itens.length === 1 && !item}>
                <FaTimes />
              </BotaoLista>
            </div>
          ))}
          <div>
            <BotaoLista type="button" $adicionar onClick={() => atualizarExtra(c.chave, [...itens, ''])}>
              <FaPlus /> {c.rotuloAdicionar || 'Adicionar'}
            </BotaoLista>
          </div>
          <HintText>Itens em branco são ignorados. Sem nenhum item, o documento sai com linhas para preencher à mão.</HintText>
        </div>
      );
    }
    if (c.tipo === 'checks') {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {c.opcoes.map((o) => (
            <label key={o.valor} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, cursor: 'pointer' }}>
              <input type="checkbox" checked={(valor || []).includes(o.valor)} onChange={() => alternarOpcao(c.chave, o.valor)} />
              {o.rotulo}
            </label>
          ))}
        </div>
      );
    }
    if (c.tipo === 'select') {
      return (
        <SelectField style={{ width: '100%' }} value={valor} onChange={(ev) => atualizarExtra(c.chave, ev.target.value)}>
          {c.opcoes.map((o) => <option key={o.valor} value={o.valor}>{o.rotulo}</option>)}
        </SelectField>
      );
    }
    if (c.tipo === 'valor') {
      return (
        <NumericFormat
          value={valor ?? ''}
          onValueChange={(v, info) => { if (info.source === 'event') atualizarExtra(c.chave, v.floatValue ?? null); }}
          thousandSeparator="." decimalSeparator="," decimalScale={2} fixedDecimalScale
          prefix="R$ " placeholder="R$ 0,00"
          customInput={Input}
        />
      );
    }
    return (
      <Input
        type={c.tipo === 'data' ? 'date' : (c.tipo === 'hora' ? 'time' : 'text')}
        value={valor}
        onChange={(ev) => atualizarExtra(c.chave, ev.target.value)}
        placeholder={c.placeholder || ''}
        hasError={!!errosExtras[c.chave]}
      />
    );
  };

  // ── Gerar / imprimir ─────────────────────────────────────────────────────
  const validar = () => {
    const erros = {};
    if (!declarante.nome.trim()) erros.nome = 'Informe o nome.';
    if (!declarante.cpfCnpj.trim()) erros.cpfCnpj = 'Informe o CPF/CNPJ.';
    setErrosDeclarante(erros);

    const temVeiculo = veiculo.placa.trim() || veiculo.chassi.trim();
    setErroVeiculo(temVeiculo ? '' : 'Informe ao menos a placa ou o chassi do veículo.');

    const errosComp = {};
    if (modelo.comprador) {
      if (!comprador.nome.trim()) errosComp.nome = 'Informe o nome do adquirente.';
      if (!comprador.cpfCnpj.trim()) errosComp.cpfCnpj = 'Informe o CPF/CNPJ do adquirente.';
    }
    setErrosComprador(errosComp);

    // Campos do documento obrigatórios (ex.: tipo de carroceria): não gera com eles em branco
    const errosDoc = {};
    modelo.campos.forEach((c) => {
      if (c.obrigatorio && !String(extras[c.chave] ?? '').trim()) errosDoc[c.chave] = `Informe: ${c.rotulo.toLowerCase()}.`;
    });
    setErrosExtras(errosDoc);

    const ok = Object.keys(erros).length === 0 && !!temVeiculo && Object.keys(errosComp).length === 0
      && Object.keys(errosDoc).length === 0;
    if (!ok) addToast('warning', 'Preencha os campos obrigatórios destacados em vermelho.');
    return ok;
  };

  const montarHtml = () => gerarDocumentoHtml(modelo, {
    declarante, comprador, veiculo, veiculoOrigem, extras, local, dataISO: dataDocumento
  }, textoCustomizado);

  // Requerimento de serviço / primeiro emplacamento: abre o formulário do DETRAN/RS (o mesmo da Nexus CRVA)
  // numa nova aba, já com os dados do requerente e do veículo. Lá se marca o que falta e imprime/salva o PDF.
  const abrirFormularioDetran = () => {
    const doc = removerMascaraCPFCNPJ(declarante.cpfCnpj);
    const payload = {
      tipoRequerimento: modelo.formularioDetran,
      cliente: {
        nome: declarante.nome,
        cpf: doc.length === 11 ? doc : '',
        cnpj: doc.length === 14 ? doc : '',
        identidade: declarante.rg,
        telefone: declarante.telefone,
        email: declarante.email,
        logradouro: declarante.logradouro,
        numero: declarante.numero,
        complemento: declarante.complemento,
        bairro: declarante.bairro,
        cidade: declarante.cidade,
        uf: declarante.uf,
        cep: formatarCep(declarante.cep)
      },
      placa: (veiculo.placa || '').toUpperCase(),
      chassi: (veiculo.chassi || '').toUpperCase(),
      marcaModelo: veiculo.marcaModelo || '',
      cidadeEmpresa: local
    };
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    window.open(`/requerimento-detran?data=${encoded}`, '_blank');
  };

  const gerarDocumento = () => {
    if (!validar()) return;
    if (modelo.formularioDetran) {
      abrirFormularioDetran();
      return;
    }
    if (mostrarPreview && editadoRef.current &&
        !window.confirm('Gerar novamente vai descartar as alterações feitas no documento. Continuar?')) {
      return;
    }
    setDocumentoHtml(sanitizarHtml(montarHtml()));
    setVersaoDocumento((v) => v + 1);
    editadoRef.current = false;
    setMostrarPreview(true);
    setTimeout(() => {
      if (previewRef.current) previewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const restaurarOriginal = () => {
    if (!window.confirm('Descartar as alterações e voltar ao texto original?')) return;
    setDocumentoHtml(sanitizarHtml(montarHtml()));
    setVersaoDocumento((v) => v + 1);
    editadoRef.current = false;
  };

  const novoDocumento = () => {
    setDeclarante(pessoaVazia());
    setComprador(pessoaVazia());
    setVeiculosDoCliente([]);
    setVeiculo(veiculoVazio());
    setVeiculoOrigem(veiculoVazio());
    setImportacaoOrigem(null);
    setImportacaoVeiculo(null);
    setOrigem({ declarante: null, comprador: null, veiculo: null });
    setExtras(extrasIniciais(modelo));
    setDataDocumento('');
    setErrosDeclarante({});
    setErrosComprador({});
    setErroVeiculo('');
    setErrosExtras({});
    setMostrarPreview(false);
  };

  // Comandos de formatação do editor (estilo Word); onMouseDown + preventDefault mantém a seleção
  const comando = (nome, valor) => {
    if (editorRef.current) editorRef.current.focus();
    document.execCommand(nome, false, valor);
  };
  const botaoEditor = (nome, valor) => ({
    type: 'button',
    onMouseDown: (ev) => { ev.preventDefault(); comando(nome, valor); }
  });

  // Colar sempre como texto puro, para não trazer fontes e cores de outros documentos
  const colarTextoPuro = (ev) => {
    ev.preventDefault();
    const texto = (ev.clipboardData || window.clipboardData).getData('text');
    document.execCommand('insertText', false, texto);
  };

  const pessoaCampos = (pessoa, atualizar, erros, alvo, setPessoa, situacao, salvando, onSalvar) => (
    <>
      <ImportarDocumento
        importacao={pessoa._importacao}
        onImportacaoChange={(imp) => atualizar('_importacao', imp)}
        onDadosLidos={(dados) => Object.entries(dados).forEach(([campo, valor]) => atualizar(campo, valor))}
      />
      <FormRow>
        <InputGroup style={{ flex: 2 }}>
          <Label>Nome*</Label>
          <Input value={pessoa.nome} onChange={(ev) => atualizar('nome', ev.target.value)} placeholder="Nome completo" hasError={!!erros.nome} />
          {erros.nome && <ErrorText>{erros.nome}</ErrorText>}
        </InputGroup>
        <InputGroup>
          <Label>CPF/CNPJ*</Label>
          <Input
            value={formatarCpfCnpj(pessoa.cpfCnpj)}
            onChange={(ev) => atualizar('cpfCnpj', removerMascaraCPFCNPJ(ev.target.value))}
            placeholder="000.000.000-00"
            hasError={!!erros.cpfCnpj}
          />
          {erros.cpfCnpj && <ErrorText>{erros.cpfCnpj}</ErrorText>}
        </InputGroup>
      </FormRow>
      <FormRow>
        <InputGroup>
          <Label>RG</Label>
          <Input value={pessoa.rg} onChange={(ev) => atualizar('rg', ev.target.value)} placeholder="0000000000" />
        </InputGroup>
        <InputGroup>
          <Label>Telefone</Label>
          <Input value={pessoa.telefone} onChange={(ev) => atualizar('telefone', ev.target.value)} placeholder="(53) 99999-9999" />
        </InputGroup>
        <InputGroup>
          <Label>E-mail</Label>
          <Input value={pessoa.email} onChange={(ev) => atualizar('email', ev.target.value)} placeholder="email@exemplo.com" />
        </InputGroup>
      </FormRow>
      <SubTitle><FaMapMarkerAlt size={12} /> Endereço</SubTitle>
      <EnderecoCampos
        valor={pessoa}
        onChange={atualizar}
        onCepChange={mudarCep(alvo, setPessoa)}
        buscando={buscandoCep === alvo}
      />
      <BarraCadastro situacao={situacao} salvando={salvando} onSalvar={onSalvar} rotulo="Salvar dados do cliente" />
    </>
  );

  const situacaoDeclarante = situacaoCadastro({
    vinculado: !!declarante.idCliente, origem: origem.declarante, atual: declarante, campos: CAMPOS_PESSOA,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda não cadastrado: será criado no cadastro.',
    camposFixos: ['cpfCnpj'], rotulosCampo: ROTULOS_CAMPO_PESSOA
  });
  const situacaoComprador = situacaoCadastro({
    vinculado: !!comprador.idCliente, origem: origem.comprador, atual: comprador, campos: CAMPOS_PESSOA,
    obrigatorios: ['nome', 'cpfCnpj'], rotuloNovo: 'Cliente ainda não cadastrado: será criado no cadastro.',
    camposFixos: ['cpfCnpj'], rotulosCampo: ROTULOS_CAMPO_PESSOA
  });
  const situacaoVeiculo = situacaoCadastro({
    vinculado: !!veiculo.idVeiculo, origem: origem.veiculo, atual: veiculo, campos: CAMPOS_VEICULO_SYNC,
    obrigatorios: ['placa', 'marcaModelo'], rotuloNovo: 'Veículo ainda não cadastrado: será criado no cadastro do declarante.',
    camposFixos: ['placa'], rotulosCampo: ROTULOS_CAMPO_VEICULO
  });

  return (
    <PageBackground className="docc-page">
      <style>{`
        @page { size: A4; margin: 0; }
        @media print {
          body * { visibility: hidden; }
          .docc-print-area, .docc-print-area * { visibility: visible; }
          .no-print { display: none !important; }
          .docc-page { min-height: 0 !important; background: none !important; }
          .docc-preview { margin: 0 !important; padding: 0 !important; max-width: none !important; }
          .docc-print-area {
            box-shadow: none !important; border: none !important; border-radius: 0 !important;
            padding: 16mm 28mm !important; box-sizing: border-box; width: 100%;
          }
        }
      `}</style>

      <div className="no-print">
        <Header />
      </div>

      <Content className="no-print">
        <TitleRow>
          <Title><FaFileAlt size={22} /> {modelo.titulo}</Title>
          <SecondaryButton onClick={() => navigate('/documentos-complementares')}>
            <FaArrowLeft /> Documentos
          </SecondaryButton>
        </TitleRow>

        {/* Cliente vindo da importação de CRLV-e nasce só com nome e CPF: avisa o que falta neste documento */}
        <AvisoCadastroIncompleto
          documento="documentoComplementar"
          pessoas={[
            { rotulo: modelo.rotuloDeclarante || 'Declarante', pessoa: declarante },
            ...(modelo.comprador ? [{ rotulo: modelo.rotuloComprador || 'Adquirente', pessoa: comprador }] : []),
          ]}
        />

        {/* ── Declarante / proprietário ─────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaUser size={16} /> {modelo.rotuloDeclarante || (modelo.comprador ? 'Proprietário vendedor' : 'Declarante / proprietário')}</CardTitle>
            <SearchButton onClick={() => abrirBusca('declarante')}>
              <FaSearch size={12} /> Buscar Cliente Cadastrado
            </SearchButton>
          </CardHeader>
          {pessoaCampos(declarante, atualizarDeclarante, errosDeclarante, 'declarante', setDeclarante, situacaoDeclarante, salvandoDeclarante, salvarClienteNoCadastro('declarante'))}
        </Card>

        {/* ── Adquirente (só nos documentos de venda) ───────────────────── */}
        {modelo.comprador && (
          <Card>
            <CardHeader>
              <CardTitle><FaUser size={16} /> Adquirente (comprador)</CardTitle>
              <SearchButton onClick={() => abrirBusca('comprador')}>
                <FaSearch size={12} /> Buscar Cliente Cadastrado
              </SearchButton>
            </CardHeader>
            {pessoaCampos(comprador, atualizarComprador, errosComprador, 'comprador', setComprador, situacaoComprador, salvandoComprador, salvarClienteNoCadastro('comprador'))}
          </Card>
        )}

        {/* ── Veículo ────────────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle><FaCar size={16} /> {modelo.rotuloVeiculo || 'Veículo'}</CardTitle>
            <SearchButton onClick={() => abrirModalVeiculo('principal')}>
              <FaSearch size={12} /> Selecionar Veículo Cadastrado
            </SearchButton>
          </CardHeader>
          <ImportarDocumento
            tipo="veiculo"
            importacao={importacaoVeiculo}
            onImportacaoChange={setImportacaoVeiculo}
            onDadosLidos={aplicarDocumentoVeiculo}
          />
          <FormRow>
            <InputGroup>
              <Label>Placa</Label>
              <Input value={veiculo.placa} onChange={(ev) => atualizarVeiculo('placa', ev.target.value.toUpperCase())} placeholder="AAA0A00" hasError={!!erroVeiculo} />
            </InputGroup>
            <InputGroup style={{ flex: 2 }}>
              <Label>Chassi</Label>
              <Input value={veiculo.chassi} onChange={(ev) => atualizarVeiculo('chassi', ev.target.value.toUpperCase())} placeholder="17 caracteres" hasError={!!erroVeiculo} />
            </InputGroup>
            <InputGroup>
              <Label>RENAVAM</Label>
              <Input value={veiculo.renavam} onChange={(ev) => atualizarVeiculo('renavam', ev.target.value)} />
            </InputGroup>
          </FormRow>
          {erroVeiculo && <ErrorText>{erroVeiculo}</ErrorText>}
          <FormRow>
            <InputGroup style={{ flex: 2 }}>
              <Label>Marca/Modelo</Label>
              <Input value={veiculo.marcaModelo} onChange={(ev) => atualizarVeiculo('marcaModelo', ev.target.value)} placeholder="Ex.: HONDA/CG 160 START" />
            </InputGroup>
            <InputGroup>
              <Label>Cor</Label>
              <Input value={veiculo.cor} onChange={(ev) => atualizarVeiculo('cor', ev.target.value)} />
            </InputGroup>
            <InputGroup>
              <Label>Ano</Label>
              <Input value={veiculo.ano} onChange={(ev) => atualizarVeiculo('ano', ev.target.value)} />
            </InputGroup>
          </FormRow>

          <BarraCadastro
            situacao={situacaoVeiculo} salvando={salvandoVeiculo} onSalvar={salvarVeiculoNoCadastro}
            rotulo="Salvar dados do veículo"
          />
        </Card>

        {/* ── Veículo de onde saiu o motor/carroceria (recibos) ─────────── */}
        {modelo.veiculoOrigem && (
          <Card>
            <CardHeader>
              <CardTitle><FaCar size={16} /> {modelo.veiculoOrigem}</CardTitle>
              <SearchButton onClick={() => abrirModalVeiculo('origem')}>
                <FaSearch size={12} /> Selecionar Veículo Cadastrado
              </SearchButton>
            </CardHeader>
            <ImportarDocumento
              tipo="veiculo"
              semGuardar
              importacao={importacaoOrigem}
              onImportacaoChange={setImportacaoOrigem}
              onDadosLidos={(dados) => setVeiculoOrigem((prev) => ({ ...prev, ...dados }))}
            />
            <FormRow>
              <InputGroup>
                <Label>Placa</Label>
                <Input value={veiculoOrigem.placa} onChange={(ev) => setVeiculoOrigem((v) => ({ ...v, placa: ev.target.value.toUpperCase() }))} placeholder="AAA0A00" />
              </InputGroup>
              <InputGroup style={{ flex: 2 }}>
                <Label>Chassi</Label>
                <Input value={veiculoOrigem.chassi} onChange={(ev) => setVeiculoOrigem((v) => ({ ...v, chassi: ev.target.value.toUpperCase() }))} placeholder="17 caracteres" />
              </InputGroup>
              <InputGroup>
                <Label>RENAVAM</Label>
                <Input value={veiculoOrigem.renavam} onChange={(ev) => setVeiculoOrigem((v) => ({ ...v, renavam: ev.target.value }))} />
              </InputGroup>
            </FormRow>
            <FormRow>
              <InputGroup style={{ flex: 2 }}>
                <Label>Marca/Modelo</Label>
                <Input value={veiculoOrigem.marcaModelo} onChange={(ev) => setVeiculoOrigem((v) => ({ ...v, marcaModelo: ev.target.value }))} placeholder="Ex.: MERCEDES-BENZ/L 1113" />
              </InputGroup>
              <InputGroup>
                <Label>Cor</Label>
                <Input value={veiculoOrigem.cor} onChange={(ev) => setVeiculoOrigem((v) => ({ ...v, cor: ev.target.value }))} />
              </InputGroup>
              <InputGroup>
                <Label>Ano</Label>
                <Input value={veiculoOrigem.ano} onChange={(ev) => setVeiculoOrigem((v) => ({ ...v, ano: ev.target.value }))} />
              </InputGroup>
            </FormRow>
          </Card>
        )}

        {/* ── Campos próprios do documento ──────────────────────────────── */}
        {modelo.campos.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle><FaFileAlt size={16} /> Dados do documento</CardTitle>
            </CardHeader>
            {modelo.campos.map((c) => (
              <FormRow key={c.chave}>
                <InputGroup>
                  <Label>{c.rotulo}{c.obrigatorio ? ' *' : ''}</Label>
                  {renderCampoExtra(c)}
                  {errosExtras[c.chave] && <ErrorText>{errosExtras[c.chave]}</ErrorText>}
                </InputGroup>
              </FormRow>
            ))}
          </Card>
        )}

        {/* ── Local e data ───────────────────────────────────────────────── */}
        <Card>
          <CardHeader>
            <CardTitle>Local e data</CardTitle>
          </CardHeader>
          <FormRow>
            <InputGroup>
              <Label>Local</Label>
              <Input value={local} onChange={(ev) => setLocal(ev.target.value)} placeholder="Canguçu-RS" />
            </InputGroup>
            <InputGroup>
              <Label>Data do documento</Label>
              <Input type="date" value={dataDocumento} onChange={(ev) => setDataDocumento(ev.target.value)} />
            </InputGroup>
          </FormRow>
          <HintText>Sem data, o documento sai com o espaço em branco para preencher à mão.</HintText>
        </Card>

        <ActionsRow>
          <PrimaryButton onClick={gerarDocumento}>
            <FaCheckCircle /> {modelo.formularioDetran ? 'Abrir Requerimento' : 'Gerar Documento'}
          </PrimaryButton>
          {mostrarPreview && (
            <SecondaryButton onClick={novoDocumento}>
              <FaArrowLeft /> Novo Documento
            </SecondaryButton>
          )}
        </ActionsRow>
      </Content>

      {/* ── Documento (preview + área de impressão) ──────────────────────── */}
      {mostrarPreview && (
        <PreviewWrapper ref={previewRef} className="docc-preview">
          <div className="no-print">
            <PreviewToolbar>
              <span>Pré-visualização — clique no texto para editar</span>
              <PrintButton onClick={() => window.print()}>
                <FaPrint /> Imprimir / Salvar PDF
              </PrintButton>
            </PreviewToolbar>
            <EditorToolbar>
              <FerramentaBtn {...botaoEditor('bold')} title="Negrito (Ctrl+B)"><FaBold /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('italic')} title="Itálico (Ctrl+I)"><FaItalic /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('underline')} title="Sublinhado (Ctrl+U)"><FaUnderline /></FerramentaBtn>
              <Separador />
              <FerramentaBtn {...botaoEditor('justifyLeft')} title="Alinhar à esquerda"><FaAlignLeft /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyCenter')} title="Centralizar"><FaAlignCenter /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyRight')} title="Alinhar à direita"><FaAlignRight /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('justifyFull')} title="Justificar"><FaAlignJustify /></FerramentaBtn>
              <Separador />
              <FerramentaBtn {...botaoEditor('undo')} title="Desfazer (Ctrl+Z)"><FaUndo /></FerramentaBtn>
              <FerramentaBtn {...botaoEditor('redo')} title="Refazer (Ctrl+Y)"><FaRedo /></FerramentaBtn>
              <Separador />
              <FerramentaBtn
                type="button"
                onClick={restaurarOriginal}
                title="Descartar alterações e voltar ao texto original"
                style={{ width: 'auto', padding: '0 10px', gap: 6 }}
              >
                <FaEraser /> Restaurar original
              </FerramentaBtn>
            </EditorToolbar>
          </div>

          <Folha
            key={versaoDocumento}
            ref={editorRef}
            className="docc-print-area"
            contentEditable
            suppressContentEditableWarning
            spellCheck
            onPaste={colarTextoPuro}
            onInput={() => { editadoRef.current = true; }}
            dangerouslySetInnerHTML={{ __html: documentoHtml }}
          />
        </PreviewWrapper>
      )}

      {/* ── Modal de busca de cliente ────────────────────────────────────── */}
      {modalBuscaAberto && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>Buscar Cliente Cadastrado</h3>
              <ModalClose onClick={() => setModalBuscaAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              <SearchRow>
                <Input
                  value={termoBusca}
                  onChange={(ev) => setTermoBusca(ev.target.value)}
                  placeholder="Nome ou CPF/CNPJ"
                  onKeyPress={(ev) => ev.key === 'Enter' && executarBusca()}
                />
                <PrimaryButton onClick={executarBusca} disabled={buscando}>
                  <FaSearch /> {buscando ? 'Buscando...' : 'Buscar'}
                </PrimaryButton>
              </SearchRow>

              {resultadosBusca.length > 0 ? (
                <ResultList>
                  {resultadosBusca.map((c) => (
                    <ResultItem key={c.idCliente} onClick={() => selecionarResultado(c)}>
                      <strong>{c.nome}</strong>
                      <span>{formatarCpfCnpj(c.cpfCnpj)}</span>
                    </ResultItem>
                  ))}
                </ResultList>
              ) : (
                <EmptyState>Nenhum resultado. Digite um nome ou CPF/CNPJ e busque.</EmptyState>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      {/* ── Modal de seleção de veículo (do cliente ou por placa) ────────── */}
      {modalVeiculoAberto && (
        <ModalOverlay className="no-print">
          <ModalBox>
            <ModalHeader>
              <h3>{alvoVeiculo === 'origem' ? modelo.veiculoOrigem : 'Selecionar Veículo'}</h3>
              <ModalClose onClick={() => setModalVeiculoAberto(false)}><FaTimes /></ModalClose>
            </ModalHeader>
            <ModalContent>
              {veiculosDoCliente.length > 0 && (
                <>
                  <SubTitle>Veículos do cliente</SubTitle>
                  <ResultList style={{ marginBottom: '18px' }}>
                    {veiculosDoCliente.map((v) => (
                      <ResultItem key={v.idVeiculo} onClick={() => importarVeiculo(v)}>
                        <strong>{v.marcaModelo}</strong>
                        <span>Placa {v.placa}{v.chassi ? ` · Chassi ${v.chassi}` : ''}</span>
                      </ResultItem>
                    ))}
                  </ResultList>
                </>
              )}

              <SubTitle>Buscar veículo cadastrado por placa</SubTitle>
              <SearchRow>
                <Input
                  value={placaBusca}
                  onChange={(ev) => setPlacaBusca(ev.target.value.toUpperCase())}
                  placeholder="AAA-0000"
                  onKeyPress={(ev) => ev.key === 'Enter' && buscarVeiculoPorPlaca()}
                />
                <PrimaryButton onClick={buscarVeiculoPorPlaca} disabled={buscandoPlaca}>
                  <FaSearch /> {buscandoPlaca ? 'Buscando...' : 'Buscar'}
                </PrimaryButton>
              </SearchRow>

              {erroPlaca && <EmptyState>{erroPlaca}</EmptyState>}

              {resultadoPlaca && (
                <ResultList>
                  <ResultItem onClick={() => importarVeiculo(resultadoPlaca)}>
                    <strong>{resultadoPlaca.marcaModelo} — {resultadoPlaca.placa}</strong>
                    <span>
                      {resultadoPlaca.cliente?.nome
                        ? `Cadastrado para: ${resultadoPlaca.cliente.nome}`
                        : 'Clique para importar os dados'}
                    </span>
                  </ResultItem>
                </ResultList>
              )}
            </ModalContent>
          </ModalBox>
        </ModalOverlay>
      )}

      <ConfirmModal
        isOpen={!!confirmacaoCadastro}
        onClose={() => setConfirmacaoCadastro(null)}
        onConfirm={() => { if (confirmacaoCadastro) confirmacaoCadastro.onConfirm(); }}
        title={confirmacaoCadastro ? confirmacaoCadastro.titulo : ''}
        message={confirmacaoCadastro ? confirmacaoCadastro.mensagem : ''}
        confirmText={confirmacaoCadastro ? confirmacaoCadastro.textoConfirmar : 'Confirmar'}
        cancelText="Cancelar"
      />
    </PageBackground>
  );
};

// Folha A4 da Procuração + estilos próprios dos requerimentos (campos, opções, caixa de declaração)
const Folha = styled(PaperSheet)`
  h1.longo { font-size: 13pt; }
  .secao { font-weight: 700; margin: 16px 0 6px 0; }
  .campo { margin: 4px 0; }
  .opcao { margin: 3px 0 3px 24px; }
  .branco { display: inline-block; min-width: 230px; border-bottom: 1px solid #333; height: 1em; }
  .caixa { border: 1px solid #333; background: #f2f2f2; padding: 8px 10px; text-align: justify; margin: 14px 0; }
  .fecha .nota { font-size: 9pt; margin: 10px 0 0 0; text-align: justify; }
`;

// Botões da lista de itens (campo tipo "lista"): "Adicionar" em destaque e "×" discreto para remover
const BotaoLista = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 8px;
  padding: ${(p) => (p.$adicionar ? '7px 14px' : '8px 10px')};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$adicionar ? '#008080' : 'white')};
  color: ${(p) => (p.$adicionar ? 'white' : '#c0392b')};
  border: 1px solid ${(p) => (p.$adicionar ? '#008080' : '#e6b0aa')};
  &:hover { opacity: 0.85; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

export default DocumentosComplementares;
